import { Workbook, Worksheet, Cell } from 'exceljs';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import moment from 'moment';
import {
  AssessmentPeriodDTO,
  ProjectAssessmentDTO,
  ProjectDTO,
  MemberPeerEvaluationDTO,
  MemberRatingDrops,
  UserEngagementDTO,
  ProjectAssessmentAverageDropsDTO
} from '@/models';
import { getUserDisplayInfo } from '@/services/user';
import { projectTypeMapping } from '@/services/project';
import { convertNumberToFourDigitsString, formatDateToDisplay, sumByField, userEngagementRoleMapping } from '@/helpers';
import {
  getActualEffort,
  getAddedValue,
  getAveragePeerRatingPoint,
  getRatingDropsFromProjectPEs,
  getProjectBaseValue,
  getReceivedDropsFromProjectPEs,
  getFinalIndividualDrops,
  getAverageSatAndComplexityByAssessmentId
} from '@/services/project-drops';
import { STYLES, NA } from './project-exporting-config';
import { DATE_FORMAT, PROJECT_TYPES } from '@/constants';

type WorksheetRow = Array<string | number>;

type MemberIdToRoleMapping = {
  [key: string]: string;
};

class ProjectExporting {
  private readonly assessmentPeriods: AssessmentPeriodDTO[] = [];
  private readonly averageSatAndComplexity: ProjectAssessmentAverageDropsDTO[];

  constructor(assessmentPeriods: AssessmentPeriodDTO[], averageSatAndComplexity: ProjectAssessmentAverageDropsDTO[]) {
    this.assessmentPeriods = assessmentPeriods;
    this.averageSatAndComplexity = averageSatAndComplexity;
  }

  public exportXLSXMultipleProjects = async (projects: ProjectDTO[]): Promise<void> => {
    if (projects.length === 1) {
      await this.exportXLSXSingleProject(projects[0]);
      return;
    }

    const zip = new JSZip();
    for (let i = 0; i < projects.length; i++) {
      const workbook = this.generateWorkbook(projects[i]);
      const data = await workbook.xlsx.writeBuffer();
      zip.file(this.buildXLSXFileName(projects[i].name), data);
    }
    const zippedContent = await zip.generateAsync({ type: 'blob' });
    saveAs(zippedContent, `HIVE_Project Assessment Results_${moment().format('YYYYMMDD')}.zip`);
  };

  public exportXLSXSingleProject = async (project: ProjectDTO): Promise<void> => {
    const workbook = this.generateWorkbook(project);
    const data = await workbook.xlsx.writeBuffer();
    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, this.buildXLSXFileName(project.name));
  };

  buildXLSXFileName = (projectName: string): string => {
    return `HIVE_${projectName}_${moment().format('YYYYMMDD')}.xlsx`;
  };

  generateWorkbook = (project: ProjectDTO): Workbook => {
    const workbook = new Workbook();
    workbook.creator = 'BEE-YOND';
    workbook.created = new Date();

    this.addProjectInfoSheetToWorkbook(workbook, project);

    [...project.projectAssessments]
      .sort((pa1, pa2) => moment(pa2.createdAt).diff(moment(pa1.createdAt)))
      .forEach((assessment: ProjectAssessmentDTO) => {
        const assessmentPeriod = this.assessmentPeriods.find(ap => ap.id === assessment.assessmentPeriodId);
        if (assessmentPeriod) {
          this.addProjectAssessmentSheetToWorkbook(workbook, project, assessment, assessmentPeriod);
        }
      });

    return workbook;
  };

  //#region ProjectInformationSheet

  private addProjectInfoSheetToWorkbook = (workbook: Workbook, project: ProjectDTO): void => {
    const ws = workbook.addWorksheet('Project Information', { properties: { defaultRowHeight: 18 } });
    ws.getColumn(1).width = 22;
    ws.getColumn(2).width = 70;

    const nameRow = ws.addRow(['Project Name', project.name]);
    nameRow.eachCell((cell: Cell) => {
      cell.border = STYLES.primaryTealBorderBottom;
    });

    const infoRows = ProjectExporting.buildProjectInfoSheetRows(project);
    infoRows.forEach((rowData: WorksheetRow, rowIdx: number) => {
      const row = ws.addRow(rowData);
      row.getCell(2).alignment = { wrapText: true };
      if (!(rowIdx % 2)) {
        row.eachCell((cell: Cell) => {
          cell.fill = STYLES.lightTealFill;
        });
      }
      if (rowIdx === infoRows.length - 1) {
        row.eachCell((cell: Cell) => {
          cell.border = STYLES.primaryTealBorderBottom;
        });
      }
    });

    ws.getColumn(1).font = STYLES.boldFont;
    ws.getColumn(2).font = STYLES.normalFont;
    ws.getColumn(1).alignment = { vertical: 'middle' };
    ws.getColumn(2).alignment = { vertical: 'middle', wrapText: true };
    nameRow.getCell(2).font = STYLES.h1Font;
  };

  static buildProjectInfoSheetRows = (project: ProjectDTO): WorksheetRow[] => {
    return [
      ['Project ID', convertNumberToFourDigitsString(project.id)],
      ['Project description', project.description],
      ['Sponsor', getUserDisplayInfo(project.sponsor)],
      ['Project manager', getUserDisplayInfo(project.manager)],
      ['Project type', projectTypeMapping[project.projectType]],
      ['Start date', formatDateToDisplay(project.startDate)],
      ['End date', formatDateToDisplay(project.endDate)]
    ];
  };

  //#endregion

  //#region ProjectAssessmentSheet

  private addProjectAssessmentSheetToWorkbook = (
    workbook: Workbook,
    project: ProjectDTO,
    assessment: ProjectAssessmentDTO,
    assessmentPeriod: AssessmentPeriodDTO
  ): void => {
    const individualTrackingProject = project.projectType === PROJECT_TYPES.INDIVIDUAL_TRACKING;
    const ws = workbook.addWorksheet(ProjectExporting.buildAssessmentSheetName(workbook, assessmentPeriod), {
      properties: { defaultRowHeight: 18 },
      views: [{ state: 'frozen', xSplit: individualTrackingProject ? 0 : 3, ySplit: 2 }]
    });
    const columnsWidth = individualTrackingProject
      ? [5, 35, 16, 16, 16, 50]
      : [5, 35, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 40, 25];
    columnsWidth.forEach((width, idx) => {
      const wsColumnIdx = idx + 1;
      ws.getColumn(wsColumnIdx).width = width;
      ws.getColumn(wsColumnIdx).alignment = { vertical: 'middle' };
    });

    this.addAssessmentHeaderRowsToWorksheet(ws, assessmentPeriod);
    ws.addRow([]);
    ws.addRow([]);

    if (individualTrackingProject) {
      this.addIndividualTrackingRowsToWorksheet(ws, assessment);
    } else {
      this.addProjectEvaluationRowsToWorksheet(ws, assessment, project.projectType);
      ws.addRow([]);
      ws.addRow([]);
      this.addMemberContributionEvaluationRowsToWorksheet(ws, assessment);
      ws.addRow([]);
      ws.addRow([]);
      this.addPeerEvaluationRowsToWorksheet(ws, assessment);
    }
  };

  static buildAssessmentSheetName = (workbook: Workbook, assessmentPeriod: AssessmentPeriodDTO): string => {
    let sheetName = assessmentPeriod.name?.replace(/[^a-zA-Z0-9 ]/g, '');
    if (workbook.worksheets.find(ws => ws && ws.name.toLowerCase() === sheetName.toLowerCase())) {
      sheetName = sheetName + ' #' + workbook.worksheets.length;
    }
    return sheetName;
  };

  //#region ProjectAssessmentSheet__HeaderRows

  private addAssessmentHeaderRowsToWorksheet = (ws: Worksheet, assessmentPeriod: AssessmentPeriodDTO) => {
    const titleRow = ws.addRow([assessmentPeriod.name]);
    const periodRow = ws.addRow([
      `Assessment period: ${formatDateToDisplay(assessmentPeriod.startDate)} - ${formatDateToDisplay(
        assessmentPeriod.endDate
      )}`
    ]);
    titleRow.font = STYLES.h1Font;
    periodRow.font = STYLES.italicFont;
    periodRow.getCell(1).border = STYLES.primaryTealBorderBottom;
    periodRow.getCell(4).border = STYLES.primaryTealBorderBottom;

    ws.mergeCells('A1:C1');
    ws.mergeCells('D1:Q1');
    ws.mergeCells('A2:C2');
    ws.mergeCells('D2:Q2');
  };

  //#endregion

  //#region ProjectAssessmentSheet__IndividualTrackingRows

  private addIndividualTrackingRowsToWorksheet = (ws: Worksheet, assessment: ProjectAssessmentDTO) => {
    const titleRow = ws.addRow(['Member Contribution Evaluation']);
    titleRow.font = STYLES.h2Font;
    ws.addRow([]);

    const headerRow = ws.addRow(['#', 'Member', 'Contribution (hour)', 'KEY', 'Final individual pollens', 'Note']);
    headerRow.eachCell((cell: Cell) => {
      cell.fill = STYLES.primaryTealFill;
      cell.font = STYLES.boldFont;
      cell.alignment = STYLES.middleCenterWrappedAlignment;
    });

    const userEngagementRows = ProjectExporting.buildIndividualTrackingRow(assessment);
    const uerLastIdx = userEngagementRows.length - 1;
    const cellIdxes = {
      member: 2,
      finalIndividualDrops: 5,
      note: 6
    };
    userEngagementRows.forEach((rowData: WorksheetRow, idx: number) => {
      const row = ws.addRow(rowData);
      row.eachCell((cell: Cell) => {
        cell.alignment = STYLES.middleRightWrappedAlignment;
        cell.font = cell.value === NA ? STYLES.naFont : STYLES.normalFont;
        if (!(idx % 2)) {
          cell.fill = STYLES.lightTealFill;
        }
        if (idx === uerLastIdx) {
          cell.border = STYLES.primaryTealBorderBottom;
        }
      });
      row.getCell(cellIdxes.member).alignment = STYLES.middleLeftWrappedAlignment;
      row.getCell(cellIdxes.note).alignment = STYLES.middleLeftWrappedAlignment;
      row.getCell(cellIdxes.finalIndividualDrops).font = STYLES.boldFont;
    });
  };

  static buildIndividualTrackingRow = (assessment: ProjectAssessmentDTO): WorksheetRow[] => {
    const userEngagements = [...assessment.userEngagements].sort((ue1, ue2) => ue2.contribution - ue1.contribution);
    return userEngagements.map((ue: UserEngagementDTO) => [
      ue.user.id + 1,
      getUserDisplayInfo(ue.user),
      ue.contribution || NA,
      assessment.key || NA,
      ue.individualDrops || NA,
      ue.note || ''
    ]);
  };

  //#endregion

  //#region ProjectAssessmentSheet__ProjectEvaluationRows

  private addProjectEvaluationRowsToWorksheet = (
    ws: Worksheet,
    assessment: ProjectAssessmentDTO,
    projectType: string
  ) => {
    const titleRow = ws.addRow(['Project Evaluation']);
    titleRow.font = STYLES.h2Font;

    const emptyRow = ws.addRow([]);
    emptyRow.getCell(2).border = STYLES.primaryTealBorderBottom;
    emptyRow.getCell(3).border = STYLES.primaryTealBorderBottom;

    const rows = this.buildProjectEvaluationRows(assessment, projectType);
    rows.forEach((rowData: WorksheetRow, rowIdx: number) => {
      const row = ws.addRow(rowData);
      if (rowIdx % 2) {
        row.getCell(2).fill = STYLES.lightTealFill;
        row.getCell(3).fill = STYLES.lightTealFill;
      }
      if (rowIdx === rows.length - 1 || rowIdx === rows.length - 2) {
        row.getCell(2).border = STYLES.primaryTealBorderBottom;
        row.getCell(3).border = STYLES.primaryTealBorderBottom;
      }
      row.getCell(2).font = STYLES.normalFont;
      row.getCell(3).font = row.getCell(3).value === NA ? STYLES.naFont : STYLES.normalFont;
      row.getCell(3).alignment = STYLES.middleRightWrappedAlignment;
    });

    ws.getCell('B14').font = STYLES.boldFont;
    ws.getCell('C14').font = STYLES.h2Font;
  };

  private buildProjectEvaluationRows = (assessment: ProjectAssessmentDTO, projectType: string): WorksheetRow[] => {
    const actualEffort = getActualEffort(projectType, assessment.plannedEffort, assessment.userEngagements);
    const projectBaseValue = getProjectBaseValue(assessment);
    const { averageComplexity, averageSat } = getAverageSatAndComplexityByAssessmentId(
      this.averageSatAndComplexity,
      assessment.id
    );
    const addedValue = getAddedValue(assessment, averageComplexity, averageSat, actualEffort);
    return [
      ['', 'Planned effort', assessment.plannedEffort || NA],
      ['', 'Actual effort', actualEffort || NA],
      ['', 'KEY', assessment.key || NA],
      ['', 'Complexity', assessment.complexity || NA],
      ['', 'SAT', assessment.sat || NA],
      ['', 'Project base value', projectBaseValue || NA],
      ['', 'Added value', addedValue || NA],
      ['', 'Final project pollens', assessment.finalDrops || NA]
    ];
  };

  //#endregion

  //#region ProjectAssessmentSheet__MemberContributionEvaluationRows

  private addMemberContributionEvaluationRowsToWorksheet = (ws: Worksheet, assessment: ProjectAssessmentDTO) => {
    const titleRow = ws.addRow(['Member Contribution Evaluation']);
    titleRow.font = STYLES.h2Font;
    ws.addRow([]);

    const headerTopRow = ws.addRow([
      '#',
      'Member',
      'Role',
      'Contribution (hour)',
      'Contribution (%)',
      'Ratings',
      '',
      '',
      '',
      '',
      '',
      'Pollens break-down',
      '',
      '',
      '',
      '',
      ''
    ]);
    const headerBottomRow = ws.addRow([
      '',
      '',
      '',
      '',
      '',
      'Average rating',
      'Accountability',
      'Communication',
      'Supportiveness',
      'Personal attributes',
      'Professional conduct',
      'Contribution pollens',
      'Giving pollens',
      'Received pollens',
      'Adjusted received pollens',
      'Reasons for adjustment',
      'Final individual pollens'
    ]);
    headerTopRow.eachCell((cell: Cell) => {
      cell.fill = STYLES.primaryTealFill;
      cell.font = STYLES.boldFont;
      cell.alignment = STYLES.middleCenterWrappedAlignment;
    });
    headerBottomRow.eachCell((cell: Cell) => {
      cell.fill = STYLES.primaryTealFill;
      cell.font = STYLES.boldFont;
      cell.alignment = STYLES.middleCenterWrappedAlignment;
    });
    ws.getCell('F19').border = { right: STYLES.greyBorder, bottom: STYLES.greyBorder }; /* cell__ratings */
    ws.getCell('L19').border = { bottom: STYLES.greyBorder }; /* cell__drops-break-down */
    ws.getCell('E19').border = { right: STYLES.greyBorder }; /* cell__contribution-% */
    ws.getCell('K20').border = { right: STYLES.greyBorder }; /* cell__professional-product */

    const userEngagementRows = ProjectExporting.buildMemberContributionEvaluationRows(assessment);
    const uerLastIdx = userEngagementRows.length - 1;
    const cellIdxes = {
      member: 2,
      role: 3,
      contributionPercent: 5,
      contributionDrops: 12,
      reasonForAdjustment: 16,
      finalIndividualDrops: 17
    };
    userEngagementRows.forEach((rowData: WorksheetRow, idx: number) => {
      const row = ws.addRow(rowData);
      row.getCell(cellIdxes.contributionPercent).numFmt = '0.00%';
      [6, 7, 8, 9, 10, 11].forEach(cellIdx => {
        row.getCell(cellIdx).numFmt = '0.0';
      });
      row.eachCell((cell: Cell) => {
        cell.alignment = STYLES.middleRightWrappedAlignment;
        cell.font = cell.value === NA ? STYLES.naFont : STYLES.normalFont;
        if (!(idx % 2)) {
          cell.fill = STYLES.lightTealFill;
        }
        if (idx === uerLastIdx) {
          cell.border = STYLES.primaryTealBorderBottom;
        }
      });
      row.getCell(cellIdxes.member).alignment = STYLES.middleLeftWrappedAlignment;
      row.getCell(cellIdxes.role).alignment = STYLES.middleLeftWrappedAlignment;
      row.getCell(cellIdxes.reasonForAdjustment).alignment = STYLES.middleLeftWrappedAlignment;
      row.getCell(cellIdxes.finalIndividualDrops).font = STYLES.boldFont;
      if (idx === uerLastIdx) {
        row.getCell(cellIdxes.contributionPercent).border = {
          right: STYLES.greyBorder,
          ...STYLES.primaryTealBorderBottom
        };
        row.getCell(cellIdxes.contributionDrops).border = {
          left: STYLES.greyBorder,
          ...STYLES.primaryTealBorderBottom
        };
      } else {
        row.getCell(cellIdxes.contributionPercent).border = { right: STYLES.greyBorder };
        row.getCell(cellIdxes.contributionDrops).border = { left: STYLES.greyBorder };
      }
    });

    ws.mergeCells('A19:A20'); /* cell__# */
    ws.mergeCells('B19:B20'); /* cell__role */
    ws.mergeCells('C19:C20'); /* cell__contribution-hours */
    ws.mergeCells('D19:D20'); /* cell__contribution-% */
    ws.mergeCells('E19:E20'); /* cell__average-rating */

    ws.mergeCells('F19:K19'); /* cell__ratings */
    ws.mergeCells('L19:Q19'); /* cell__drops-break-down */
  };

  static buildMemberContributionEvaluationRows = (assessment: ProjectAssessmentDTO): WorksheetRow[] => {
    const totalContribution = sumByField(assessment.userEngagements, 'contribution');
    const userEngagements = [...assessment.userEngagements].sort((ue1, ue2) => ue2.contribution - ue1.contribution);
    return userEngagements.map((ue: UserEngagementDTO, idx: number) => {
      const userId = ue.user.id;
      const contributionPercent = ue.contribution / totalContribution;
      const receivedDrops: number = getReceivedDropsFromProjectPEs(userId, assessment.peerEvaluations);
      const ratingDrops: MemberRatingDrops = getRatingDropsFromProjectPEs(userId, assessment.peerEvaluations);
      const averageRating = getAveragePeerRatingPoint(ratingDrops);
      const finalIndividualDrops = getFinalIndividualDrops(ue, receivedDrops);
      return [
        idx + 1,
        getUserDisplayInfo(ue.user),
        userEngagementRoleMapping[ue.userEngagementRole],
        ue.contribution || NA,
        contributionPercent || NA,
        averageRating || NA,
        ratingDrops.accountabilityPoint || NA,
        ratingDrops.communicationPoint || NA,
        ratingDrops.supportivenessPoint || NA,
        ratingDrops.personalAttributesPoint || NA,
        ratingDrops.professionalConductPoint || NA,
        ue.contributionDrops || NA,
        ue.givingDrops || NA,
        receivedDrops || NA,
        ue.adjustedDrops || NA,
        ue.reasonForAdjustment || '',
        finalIndividualDrops || NA
      ];
    });
  };

  //#endregion

  //#region ProjectAssessmentSheet__PeerEvaluationRows

  private addPeerEvaluationRowsToWorksheet = (ws: Worksheet, assessment: ProjectAssessmentDTO) => {
    const titleRow = ws.addRow(['Peer Evaluations']);
    titleRow.font = STYLES.h2Font;
    ws.addRow([]);

    const memberIdToRoleMapping: MemberIdToRoleMapping = {};
    assessment.userEngagements.forEach(ue => {
      memberIdToRoleMapping[ue.user.id] = ue.userEngagementRole;
    });

    const { userEngagements, peerEvaluations } = assessment;
    const sortedUserEngagements = [...userEngagements].sort((ue1, ue2) => ue2.contribution - ue1.contribution);
    sortedUserEngagements.forEach(ue => {
      const filteredPeerEvaluations = peerEvaluations.filter(pe => pe.user.id === ue.user.id);
      this.addMemberPERowsToWorksheet(ws, ue, userEngagements, filteredPeerEvaluations, memberIdToRoleMapping);
      ws.addRow([]);
    });
  };

  private addMemberPERowsToWorksheet = (
    ws: Worksheet,
    currentUE: UserEngagementDTO,
    userEngagements: UserEngagementDTO[],
    peerEvaluations: MemberPeerEvaluationDTO[],
    memberIdToRoleMapping: MemberIdToRoleMapping
  ) => {
    const userRow = ws.addRow([getUserDisplayInfo(currentUE.user)]);
    userRow.font = STYLES.h3Font;

    const headerRow = ws.addRow([
      '#',
      'Peer',
      'Role',
      'Accountability',
      'Communication',
      'Supportiveness',
      'Personal attributes',
      'Professional conduct',
      'Giving pollens',
      'Keep-doing comments',
      '',
      '',
      'Stop-doing comments',
      '',
      ''
    ]);
    headerRow.eachCell((cell: Cell) => {
      cell.fill = STYLES.lightTealFill;
      cell.font = STYLES.boldFont;
      cell.alignment = STYLES.middleCenterWrappedAlignment;
      cell.border = { top: STYLES.primaryTealBorder, bottom: STYLES.primaryTealBorder };
    });
    const headerRowNumber = headerRow.number;
    ws.mergeCells(`J${headerRowNumber}:L${headerRowNumber}`);
    ws.mergeCells(`M${headerRowNumber}:O${headerRowNumber}`);

    const rows = ProjectExporting.buildMemberPERows(currentUE, userEngagements, peerEvaluations, memberIdToRoleMapping);
    rows.forEach((rowData: WorksheetRow, idx: number) => {
      const row = ws.addRow(rowData);
      row.eachCell((cell: Cell) => {
        cell.font = cell.value === NA ? STYLES.naFont : STYLES.normalFont;
        cell.alignment = STYLES.middleLeftWrappedAlignment;
        if (idx % 2) {
          cell.fill = STYLES.lightTealFill;
        }
        if (idx === rows.length - 1) {
          cell.border = STYLES.primaryTealBorderBottom;
        }
      });
      [4, 5, 6, 7, 8, 9].forEach(rowIdx => {
        row.getCell(rowIdx).alignment = STYLES.middleRightWrappedAlignment;
      });

      const rowNumber = row.number;
      ws.mergeCells(`J${rowNumber}:L${rowNumber}`);
      ws.mergeCells(`M${rowNumber}:O${rowNumber}`);
    });
  };

  static buildMemberPERows = (
    currentUE: UserEngagementDTO,
    userEngagements: UserEngagementDTO[],
    peerEvaluations: MemberPeerEvaluationDTO[],
    memberIdToRoleMapping: MemberIdToRoleMapping
  ): WorksheetRow[] => {
    return userEngagements
      .filter(ue => ue.user.id !== currentUE.user.id)
      .map((ue, idx) => {
        const pe = peerEvaluations.find(p => p.recipient.id === ue.user.id) || ({} as MemberPeerEvaluationDTO);
        return [
          idx + 1,
          getUserDisplayInfo(ue.user),
          userEngagementRoleMapping[memberIdToRoleMapping[ue.user.id]],
          pe.accountabilityPoint || NA,
          pe.communicationPoint || NA,
          pe.supportivenessPoint || NA,
          pe.personalAttributesPoint || NA,
          pe.professionalConductPoint || NA,
          pe.givingDrops || NA,
          pe.keepDoing || '',
          '',
          '',
          pe.stopDoing || '',
          '',
          ''
        ];
      });
  };

  // #endregion

  // #endregion
}

export default ProjectExporting;
